import { Injectable, OnDestroy } from '@angular/core';
import { environment } from '@env/environment';
import { AppReleaseControllerService } from '@api-swagger-client/api/appReleaseController.service';
import { BehaviorSubject, Subscription, catchError, interval, of, startWith, switchMap } from 'rxjs';

export type AppUpdateStatus = 'ok' | 'update_available' | 'update_required';

export interface AppUpdateState {
    status: AppUpdateStatus;
    currentVersion: string;
    latestVersion: string | null;
    notes: string | null;
}

/** Cada cuánto se vuelve a preguntar: una pestaña abierta días enteros también se entera. */
const CHECK_INTERVAL_MS = 30 * 60 * 1000;

/**
 * Compara la versión de este bundle contra los releases publicados en el backend
 * (`app_releases`, plataforma `web`). Es el mismo endpoint que usa la app móvil, así
 * que publicar una versión nueva se administra igual en los tres clientes.
 */
@Injectable({ providedIn: 'root' })
export class AppUpdateService implements OnDestroy {
    readonly currentVersion = (environment.version ?? '').split('-')[0];

    private readonly stateSubject = new BehaviorSubject<AppUpdateState>({
        status: 'ok',
        currentVersion: this.currentVersion,
        latestVersion: null,
        notes: null,
    });
    readonly state$ = this.stateSubject.asObservable();

    private polling?: Subscription;

    constructor(private readonly appReleaseController: AppReleaseControllerService) {}

    startChecking(): void {
        if (this.polling || !this.currentVersion) return;
        this.polling = interval(CHECK_INTERVAL_MS)
            .pipe(
                startWith(0),
                switchMap(() =>
                    this.appReleaseController.appReleaseControllerCheck('web', this.currentVersion).pipe(
                        // Si el backend no responde se sigue como si todo estuviera al día:
                        // este aviso nunca debe impedir usar el sistema.
                        catchError(() => of(null))
                    )
                )
            )
            .subscribe((response) => {
                const data = response?.data;
                if (!data?.status) return;
                this.stateSubject.next({
                    status: data.status as AppUpdateStatus,
                    currentVersion: this.currentVersion,
                    latestVersion: data.latestVersion ?? null,
                    notes: data.notes ?? null,
                });
            });
    }

    /**
     * Trae la versión nueva sin cerrar la sesión: solo se descartan las cachés del
     * navegador y se recarga. Los tokens viven en localStorage y se conservan.
     */
    async reloadToLatest(): Promise<void> {
        await this.clearBrowserCaches();
        window.location.reload();
    }

    /**
     * Limpieza completa (localStorage incluido): también cierra la sesión. Es lo que
     * hacía el botón "Actualizar Aplicación" del panel de configuración.
     */
    async clearAllAndReload(): Promise<void> {
        localStorage.clear();
        localStorage.setItem('appVersion', this.currentVersion);
        sessionStorage.clear();
        await this.clearBrowserCaches();
        window.location.reload();
    }

    ngOnDestroy(): void {
        this.polling?.unsubscribe();
    }

    private async clearBrowserCaches(): Promise<void> {
        if (!('caches' in window)) return;
        const names = await caches.keys();
        await Promise.all(names.map((name) => caches.delete(name)));
    }
}
